import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Lightbulb, Rocket, FileText, PlayCircle, Newspaper, ClipboardList, ExternalLink } from "lucide-react";
import { Container, PageFade, Reveal } from "../components/site/Motion";
import { PageHeader } from "../components/site/Sections";
import { api } from "../lib/api";
import { TaxCalculator } from "../components/site/TaxCalculator";
import { RiskProfile } from "../components/site/RiskProfile";

export const RES_TYPES = { article: { l: "Article", i: Newspaper }, video: { l: "Vidéo", i: PlayCircle }, guide: { l: "Guide PDF", i: FileText }, fiche: { l: "Fiche explicative", i: ClipboardList } };
export const RES_CATS = { apprendre: "Apprendre", comprendre: "Comprendre", agir: "Passer à l'action" };
export const RES_AUD = { tous: "Tous", autonomes: "Travailleurs autonomes", incorpores: "Entrepreneurs incorporés", epargne: "Épargne", protection: "Protection", retraite: "Retraite" };

const CATS = [
  { id: "apprendre", icon: BookOpen, t: "Apprendre", d: "Guides et explications accessibles." },
  { id: "comprendre", icon: Lightbulb, t: "Comprendre", d: "Concepts financiers et exemples pratiques." },
  { id: "agir", icon: Rocket, t: "Passer à l'action", d: "Outils de préparation et prise de rendez-vous." },
];

const ResourceCard = ({ r }) => {
  const T = RES_TYPES[r.type];
  return (
    <article className="card-soft flex h-full flex-col p-6" data-testid={`resource-card-${r.id}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#650D23]"><T.i className="h-4 w-4" />{T.l}</div>
      <h3 className="mt-3 text-xl">{r.title}</h3>
      <p className="mt-2 flex-1 text-sm text-[#242020]/70">{r.summary}</p>
      <div className="mt-4 flex items-center justify-between text-xs text-[#242020]/60">
        <span>{RES_AUD[r.audience]}</span>
        {r.is_demo && <span className="rounded-full bg-[#EDE3D9] px-2.5 py-1 text-[#650D23]" data-testid="resource-demo-badge">Démonstration</span>}
      </div>
      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#650D23]">Consulter <ExternalLink className="h-3.5 w-3.5" /></a>}
    </article>
  );
};

export default function PlansEducatifs() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  const [aud, setAud] = useState("");
  useEffect(() => {
    api.get("/resources").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  const shown = useMemo(() => items.filter((r) => (!cat || r.category === cat) && (!aud || r.audience === aud)), [items, cat, aud]);

  return (
    <PageFade>
      <PageHeader eyebrow="Plans éducatifs" title="Comprendre aujourd'hui" accent="pour mieux décider demain." image="/img/mental4.jpg">
        <p className="max-w-xl text-lg text-[#242020]/80">Des ressources simples et pédagogiques pour mieux comprendre vos finances, à votre rythme.</p>
      </PageHeader>
      <Container className="py-16">
        <div className="grid gap-6 md:grid-cols-3">
          {CATS.map(({ id, icon: I, t, d }, i) => (
            <Reveal key={id} delay={i * 0.08}>
              <button onClick={() => setCat(cat === id ? "" : id)} data-testid={`edu-cat-${id}`}
                className={`flex h-full w-full flex-col rounded-2xl border p-8 text-left transition-colors ${cat === id ? "border-[#4A0C1B] bg-[#650D23] text-[#FFFDFB]" : "card-soft"}`}>
                <I className="h-7 w-7" strokeWidth={1.5} />
                <h2 className="mt-5 text-2xl uppercase tracking-wide">{t}</h2>
                <p className={`mt-2 ${cat === id ? "text-[#EDE3D9]" : "text-[#242020]/70"}`}>{d}</p>
              </button>
            </Reveal>
          ))}
        </div>
        <div className="mt-14"><Reveal><TaxCalculator /></Reveal></div>
        <div className="mt-10"><Reveal><RiskProfile /></Reveal></div>
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-3xl">Bibliothèque de ressources</h2>
          <div className="flex flex-wrap gap-2" data-testid="edu-audience-filters">
            {[["", "Toutes"], ...Object.entries(RES_AUD).filter(([k]) => k !== "tous")].map(([k, l]) => (
              <button key={k || "all"} onClick={() => setAud(k)} data-testid={`edu-aud-${k || "all"}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${aud === k ? "border-[#650D23] bg-[#650D23] text-[#FFFDFB]" : "border-[#EDE3D9] hover:border-[#650D23]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="resource-list">
          {shown.map((r) => <ResourceCard key={r.id} r={r} />)}
        </div>
        {!shown.length && <p className="mt-8 text-[#242020]/60" data-testid="resource-empty">Aucune ressource dans cette catégorie pour le moment. De nouveaux contenus seront ajoutés prochainement.</p>}
        <div className="mt-14 text-center">
          <Link to="/consultation" className="btn-primary" data-testid="edu-consultation-cta">Prendre rendez-vous <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </Container>
    </PageFade>
  );
}
